import {Button} from 'antd';
import DOMPurify from 'dompurify';
import React, {useMemo, useRef, useState} from 'react';
import {generatePath} from 'react-router';

import {appRoute} from '../../../../../../../app-route';
import {Locale} from '../../../../../../../provider/locale/locale';
import {useSystem} from '../../../../../../../provider/system/system-hook';
import {toTrimmedString} from '../../../../../../../util/string';
import {PreviewGroup} from '../preview-group/preview-group';
import {
    buildReviewCommentHtmlWithSearchMarks,
    constructComment,
    constructCommentPreview,
    convertCatalogCommentAttributes,
    extractReviewCommentContent,
    hasProblemSolvedInComment,
    linkifyCommentHtml,
    ReviewProblemSolvedBadge,
} from '../review-helper';

import * as styles from './new-review-comment.scss';

const CATALOG_ID_ASN_NEWS = 213;

type PropsType = {
    comment: string | null;
    reviewId: number;
    catalogId?: number;
};

export function NewReviewComment(props: PropsType): JSX.Element {
    const {comment = '', reviewId, catalogId} = props;
    const [expanded, setExpanded] = useState(false);
    const observerRef = useRef<HTMLDivElement>(null);
    const {screen} = useSystem();
    const displayComment = useMemo(
        () => convertCatalogCommentAttributes(comment || '', catalogId),
        [comment, catalogId]
    );
    const commentData = useMemo(
        () =>
            catalogId === CATALOG_ID_ASN_NEWS
                ? extractReviewCommentContent(comment || null)
                : extractReviewCommentContent(displayComment || null),
        [comment, catalogId, displayComment]
    );
    const commentPreview = useMemo(() => constructCommentPreview(displayComment || ''), [displayComment]);
    const hasHighlightMarkup = useMemo(() => /<mark[\s>]/i.test(displayComment || ''), [displayComment]);
    const isCatalogAsnNews = catalogId === CATALOG_ID_ASN_NEWS;
    const showProblemSolvedBadge = hasProblemSolvedInComment({comment, catalogId});

    const MAX_LENGTH = screen.isMobile ? 250 : 500;

    const isLong = commentPreview.length > MAX_LENGTH;
    const visibleText = !expanded && isLong ? commentPreview.slice(0, MAX_LENGTH) + '…' : commentPreview;
    const renderedCommentHtml = useMemo(
        () =>
            linkifyCommentHtml(
                hasHighlightMarkup
                    ? buildReviewCommentHtmlWithSearchMarks(displayComment || '', visibleText)
                    : DOMPurify.sanitize(visibleText)
            ),
        [displayComment, hasHighlightMarkup, visibleText]
    );

    function onCommentClickHandler(event: React.MouseEvent<HTMLElement>) {
        event.stopPropagation();

        const isLinkClick = event.target instanceof Element && event.target.closest('a') !== null;
        const hasSelectedText = toTrimmedString(window.getSelection()?.toString()) !== '';

        if (isLinkClick || hasSelectedText) {
            return;
        }

        window.open(location.origin + generatePath(appRoute.reviewsManagementReviewItem.path, {id: reviewId}));
    }

    if (isCatalogAsnNews && comment) {
        return (
            <div className={styles.Review_comment} ref={observerRef}>
                {showProblemSolvedBadge && <ReviewProblemSolvedBadge />}
                <div
                    className={styles.Review_commentText}
                    onClick={onCommentClickHandler}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onCommentClickHandler(event as unknown as React.MouseEvent<HTMLElement>);
                        }
                    }}
                    role="button"
                    tabIndex={0}
                >
                    <div>{constructComment({text: comment, withLinks: false, catalogId})}</div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.Review_comment} ref={observerRef}>
            <div className={styles.Review_commentText}>
                <div>
                    {displayComment ? (
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                        <div
                            className={styles.Review_text}
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{__html: renderedCommentHtml}}
                            onClick={onCommentClickHandler}
                        />
                    ) : (
                        <span className={styles.Review_isEmptyReview}>
                            <Locale stringKey="REVIEWS__REVIEW__NO_TEXT" />
                        </span>
                    )}
                    {isLong && (
                        <Button onClick={() => setExpanded((previousState) => !previousState)} size="small" type="link">
                            <Locale
                                stringKey={
                                    !expanded ? 'REVIEWS__REVIEW_ABUSE__SHOW_TEXT' : 'REVIEWS__REVIEW_ABUSE__HIDE_TEXT'
                                }
                            />
                        </Button>
                    )}
                </div>
            </div>
            <PreviewGroup images={commentData?.images} videos={commentData?.videos} />
        </div>
    );
}
